import { info } from "../logging/info";
import { TextValide } from "../stringhelper/TextValide";

import Event = createjs.Event;
import FocusEvent = createjs.FocusEvent;
import TextEvent = createjs.TextEvent;
import { TextField } from "@goodgamestudios/createjs-utilities";


/**
 * This class listens to the change-Events of a TextFields
 * It will immediately call functions when invalid characters are entered
 * and it will check the length or E-Mail-Validation on FocusOut
 */
export class TextFieldWatcher {
    // instance
    private static _textFieldWatcher: TextFieldWatcher;

    // errorcodes
    public static ERRORCODE_ALL_OK: number = 0;
    public static ERRORCODE_TOO_SHORT: number = 1; // only on FocusChange
    public static ERRORCODE_INVALID_CHARS: number = 2; // while tipping
    public static ERRORCODE_NO_EMAIL: number = 3; // only on FocusChange
    public static ERRORCODE_EMPTY_TEXTFIELD: number = 5;

    // rule-consts
    public static RULE_EMAIL: number = 1;
    public static RULE_TEXT: number = 2;
    public static RULE_USERNAME: number = 3; // text without ?

    private static MIN_MAIL_LENGTH: number = 6;
    private static MAX_MAIL_LENGTH: number = 50;

    // directory-names
    private static DICTIONARY_RULES: string = "_rules";
    private static DICTIONARY_CHANGE_FUNCTION: string = "_functionOk";
    private static DICTIONARY_MIN_LENGTH: string = "_minLength";
    private static DICTIONARY_VALIDATE_ON_INPUT: string = "_validate";

    // watched textfields
    private static textFieldParams: Map<any, any>;

    constructor() {
        TextFieldWatcher.textFieldParams = new Map<any, any>();
    }

    /*
     * FUNCTIONS TO USE
     */

    /**
     * watchTextField
     * Listens to typing actions of this textfields
     * and checks if the text is correct
     * then executes functions with errorcodes
     *
     * listeners will be removed automatically if textfield gets removed from stage.
     * Use stopWatchingTextField() for better control
     *
     * @param textField: the TextField which will be observed
     * @param inputRules: RULE_MAIL or RULE_TEXT. MAIL will check if the String is a correct E-Mail adress
     * @param onChangeFunction: this function will be called when the user inputs text into the TextField
     * @param minLength: use this for password validation (for example)! If the textlength is smaller than this the Errorcode TOO_SHORT will be thrown
     * @param validateOnInput: only if this is true, then all validity-checks will be execute on input
     *
     */
    public static watchTextField(textField: TextField, inputRules: number, onChangeFunction: Function, minLength: number = 0, validateOnInput: boolean = false): void {
        textField.addEventListener(FocusEvent.FOCUS_OUT, this.bindFunction(TextFieldWatcher.instance.onFocusOut));
        textField.addEventListener(FocusEvent.FOCUS_IN, this.bindFunction(TextFieldWatcher.instance.onValueChanged));
        // textField.addEventListener(FocusEvent.KEY_FOCUS_CHANGE, this.bindFunction(TextFieldWatcher.instance.onFocusOut));
        textField.addEventListener(Event.CHANGE, this.bindFunction(TextFieldWatcher.instance.onValueChanged));
        textField.addEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(TextFieldWatcher.instance.onTextFieldRemoved));

        info("Watching TextField " + textField.name);

        const listeners = new Map<any, any>();
        listeners.set(TextFieldWatcher.DICTIONARY_RULES, inputRules);
        listeners.set(TextFieldWatcher.DICTIONARY_CHANGE_FUNCTION, onChangeFunction);
        listeners.set(TextFieldWatcher.DICTIONARY_MIN_LENGTH, minLength);
        listeners.set(TextFieldWatcher.DICTIONARY_VALIDATE_ON_INPUT, validateOnInput);
        TextFieldWatcher.textFieldParams.set(textField, listeners);
    }

    /**
     * stopWatchingTextField
     * Stops listening to useractions of this textfield.
     * This function will be auto-executed when the TextFields loses its stage
     *
     * @param textField: the TextField which won't be observed anymore
     */
    public static stopWatchingTextField(textField: TextField): void {
        if (TextFieldWatcher.textFieldParams && TextFieldWatcher.textFieldParams.get(textField)) {
            if (textField.hasEventListener(TextEvent.TEXT_INPUT)) {
                textField.removeEventListener(TextEvent.TEXT_INPUT, this.bindFunction(TextFieldWatcher.instance.onValueChanged));
            }

            if (textField.hasEventListener(FocusEvent.FOCUS_IN)) {
                textField.removeEventListener(FocusEvent.FOCUS_IN, this.bindFunction(TextFieldWatcher.instance.onValueChanged));
            }

            if (textField.hasEventListener(FocusEvent.FOCUS_OUT)) {
                textField.removeEventListener(FocusEvent.FOCUS_OUT, this.bindFunction(TextFieldWatcher.instance.onFocusOut));
            }

            // if (textField.hasEventListener(FocusEvent.KEY_FOCUS_CHANGE)) {
            //     textField.removeEventListener(FocusEvent.KEY_FOCUS_CHANGE, this.bindFunction(TextFieldWatcher.instance.onFocusOut));
            // }

            if (textField.hasEventListener(Event.REMOVED_FROM_STAGE)) {
                textField.addEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(TextFieldWatcher.instance.onTextFieldRemoved));
            }

            info("Stopped watching TextField " + textField.name);
            TextFieldWatcher.textFieldParams.delete(textField);
        }
    }

    /**
     * validateTextField
     * checks a String without watching it
     * */
    public static validateString(text: string, rule: number, minLength: number): number {
        return Math.max(TextFieldWatcher.checkCharacters(text, rule), TextFieldWatcher.checkValidity(text, rule, minLength));
    }

    /*
     * USED FUNCTIONS
     * ONLY BY THIS CLASS
     */

    private onFocusOut(event: Event = null): void {
        let tf: TextField = (<TextField>event.target);
        const textFieldParams:Map<any, any> = TextFieldWatcher.textFieldParams.get(tf);
        let minLength: number = textFieldParams.get(TextFieldWatcher.DICTIONARY_MIN_LENGTH);
        let rule: number = textFieldParams.get(TextFieldWatcher.DICTIONARY_RULES);
        let errorCode: number = Math.max(TextFieldWatcher.checkCharacters(tf.text, rule), TextFieldWatcher.checkValidity(tf.text, rule, minLength));
        textFieldParams.get(TextFieldWatcher.DICTIONARY_CHANGE_FUNCTION).apply(null,[errorCode]);
    }

    private onValueChanged(event: Event = null): void {
        let tf: TextField = (<TextField>event.target);
        const textFieldParams:Map<any, any> = TextFieldWatcher.textFieldParams.get(tf);
        let minLength: number = textFieldParams.get(TextFieldWatcher.DICTIONARY_MIN_LENGTH);
        let rule: number = textFieldParams.get(TextFieldWatcher.DICTIONARY_RULES);
        let errorCode: number = TextFieldWatcher.checkCharacters(tf.text, rule);

        if (textFieldParams.get(TextFieldWatcher.DICTIONARY_VALIDATE_ON_INPUT)) {
            errorCode = Math.max(errorCode, TextFieldWatcher.checkValidity(tf.text, rule, minLength));
        }

        textFieldParams.get(TextFieldWatcher.DICTIONARY_CHANGE_FUNCTION).apply(null,[errorCode]);
    }

    /**
     * Check for invalid characters
     * */
    private static checkCharacters(text: string, rule: number): number {
        let trimmedText: string = this.trimString(text);

        if (trimmedText.length > 0 && !this.isSmartFoxValide(trimmedText)) {
            return TextFieldWatcher.ERRORCODE_INVALID_CHARS;
        }

        if (trimmedText.length > 0 && rule == TextFieldWatcher.RULE_USERNAME && !TextFieldWatcher.isValidUsername(trimmedText)) {
            return TextFieldWatcher.ERRORCODE_INVALID_CHARS;
        }

        return TextFieldWatcher.ERRORCODE_ALL_OK;
    }

    /**
     * Check for wrong length or invalid mailadress
     * */
    private static checkValidity(text: string, rule: number, minLength: number = 0): number {
        let trimmedText: string = TextFieldWatcher.trimString(text);

        if (trimmedText.length <= 0) {
            return TextFieldWatcher.ERRORCODE_EMPTY_TEXTFIELD;
        }

        if (trimmedText.length < minLength) {
            return TextFieldWatcher.ERRORCODE_TOO_SHORT;
        }

        if (rule == TextFieldWatcher.RULE_EMAIL && !TextFieldWatcher.isEmailString(trimmedText)) {
            return TextFieldWatcher.ERRORCODE_NO_EMAIL;
        }

        return TextFieldWatcher.ERRORCODE_ALL_OK;
    }

    /**
     * If TextField gets removed
     * remove all listener
     * */
    private onTextFieldRemoved(event: Event = null): void {
        TextFieldWatcher.stopWatchingTextField((<TextField>event.target));
    }

    /**
     * instance for private use only
     * */
    private static get instance(): TextFieldWatcher {
        if (!TextFieldWatcher._textFieldWatcher) {
            TextFieldWatcher._textFieldWatcher = new TextFieldWatcher();
        }

        return TextFieldWatcher._textFieldWatcher;
    }

    /*
     * VALIDATION FUNCTIONS
     */

    /**
     * returns whether the text is Smartfox validated
     * text must not be empty and not contain followed characters: + %
     */
    private static isSmartFoxValide(value: string): boolean {
        if (value.trim() == "") {
            return false;
        }

        for (let char of TextValide.invalidChars) {
            if (value.indexOf(char) >= 0) {
                return false;
            }
        }
        return true;
    }

    private static isValidUsername(value: string): boolean {
        for (let char of TextValide.invalidUsernameChars) {
            if (value.indexOf(char) >= 0) {
                return false;
            }
        }

        // Name must contain at least one letter
        let pattern: RegExp = new RegExp("^[0-9]+$");
        if (pattern.exec(value)) {
            // No letters, name is not correct
            return false;
        }

        return true;
    }

    private static isEmailString(emailString: string): boolean {
        if (emailString.length < TextFieldWatcher.MIN_MAIL_LENGTH || emailString.length > TextFieldWatcher.MAX_MAIL_LENGTH) {
            return false;
        }

        //let validEmailRegExp:RegExp = /^[\w\-]+(\.[\w\-]+)*@([A-Za-z0-9-]+\.)+[A-Za-z]{2,4}$/;
        let validEmailRegExp: RegExp = /^[A-Za-z0-9\-+_]+(\.[A-Za-z0-9\-+_]+)*@([A-Za-z0-9\-]+\.)+[A-Za-z]{2,}$/;
        let ok: boolean = validEmailRegExp.test(emailString);
        return ok;
    }

    private static trimString(text: string): string {
        return text.trim();
    }
}
